import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { PartnerService } from '@duck/api-definitions'
import { OrganizationTableFilters } from './OrganizationTableFilters'
import { OrganizationsTable } from './OrganizationsTable'
import { baseColumns, type Column } from './utils'
import { useTar } from '@/modules'
import { useAdminOrganizations } from '@/hooks/useAdminOrganizations'
import { useAllPartnerUsers } from '@/hooks/useAllPartnerUsers'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

const STORAGE_KEY = 'admin-organization-filters'

interface FilterState {
    sortField: keyof PartnerService.Organization
    sortDirection: 'asc' | 'desc'
    filterText: string
    typeFilter: PartnerService.OrganizationType[]
    activeFilter: boolean | null
    visibleColumnKeys: string[]
}

// Utility functions for localStorage
const loadFiltersFromStorage = (): FilterState | null => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        return stored ? JSON.parse(stored) : null
    } catch {
        return null
    }
}

const saveFiltersToStorage = (filters: FilterState): void => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
    } catch {
        // Ignore localStorage errors
    }
}

const reconstructColumns = (visibleKeys?: string[]): Column[] => {
    let keys = visibleKeys || baseColumns.filter(col => col.visible).map(col => col.key)
    // Migrate the legacy 'active' (Status) column, replaced by 'partners'. A user who
    // had Status visible should see Partners visible; once they toggle it off, saved
    // keys no longer contain 'active', so this migration becomes a no-op.
    if (keys.includes('active') && !keys.includes('partners')) {
        keys = [...keys, 'partners']
    }
    return baseColumns.map(col => ({
        ...col,
        visible: keys.includes(col.key)
    }))
}

export const OrganizationsManagement: React.FC = () => {
    const { t } = useTar()
    const navigate = useNavigate()
    useDocumentTitle('Organizations', 'admin')
    // Initialize state from localStorage or defaults
    const storedFilters = loadFiltersFromStorage()

    const [sortField, setSortField] = useState<keyof PartnerService.Organization>(
        storedFilters?.sortField ?? 'name'
    )
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
        storedFilters?.sortDirection ?? 'asc'
    )
    const [filterText, setFilterText] = useState(storedFilters?.filterText ?? '')
    const [typeFilter, setTypeFilter] = useState<PartnerService.OrganizationType[]>(
        Array.isArray(storedFilters?.typeFilter)
            ? (storedFilters?.typeFilter as PartnerService.OrganizationType[])
            : []
    )
    const [activeFilter, setActiveFilter] = useState<boolean | null>(
        storedFilters?.activeFilter !== undefined ? storedFilters?.activeFilter : null
    )
    const [columnDropdownOpen, setColumnDropdownOpen] = useState(false)
    const [columns, setColumns] = useState<Column[]>(
        () => reconstructColumns(storedFilters?.visibleColumnKeys)
    )

    // Save to localStorage whenever filter state changes
    useEffect(() => {
        const visibleColumnKeys = columns.filter(col => col.visible).map(col => col.key)
        const filterState: FilterState = {
            sortField,
            sortDirection,
            filterText,
            typeFilter,
            activeFilter,
            visibleColumnKeys,
        }
        saveFiltersToStorage(filterState)
    }, [sortField, sortDirection, filterText, typeFilter, activeFilter, columns])

    // Map UI filters to backend query filters
    const queryFilters = useMemo(() => {
        const filters: {
            type?: PartnerService.OrganizationType
            active?: boolean
        } = {}

        if (typeFilter.length === 1) {
            filters.type = typeFilter[0]
        }

        if (activeFilter !== null) {
            filters.active = activeFilter
        }

        return filters
    }, [typeFilter, activeFilter])

    const {
        organizations,
        hasMore,
        isInitialLoading,
        isFetchingNextPage,
        isError,
        fetchNextPage,
    } = useAdminOrganizations({
        queryFilters,
        sortField,
        sortDirection,
    })

    const { partnersByOrgId, isLoading: isPartnersLoading } = useAllPartnerUsers()

    const filteredOrganizations = useMemo(() => {
        let filtered = organizations

        // Text filter (client-side for now)
        if (filterText) {
            const searchTerm = filterText.toLowerCase()
            filtered = filtered.filter(organization =>
                organization.name.toLowerCase().includes(searchTerm) ||
                (organization.description && organization.description.toLowerCase().includes(searchTerm))
            )
        }

        // Type filter (client-side when multi-select)
        if (typeFilter.length > 1) {
            const typeSet = new Set(typeFilter)
            filtered = filtered.filter(organization => typeSet.has(organization.type))
        }

        return filtered
    }, [organizations, filterText, typeFilter])

    const handleLoadMorePage = useCallback(() => {
        if (!hasMore) return
        void fetchNextPage()
    }, [hasMore, fetchNextPage])

    const handleSort = (field: keyof PartnerService.Organization) => {
        if (field === sortField) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('asc')
        }
    }

    const toggleColumnVisibility = (columnKey: string) => {
        setColumns(prev => prev.map(col =>
            col.key === columnKey
                ? { ...col, visible: !col.visible }
                : col
        ))
    }

    const handleCreateNewOrganization = () => {
        void navigate({ to: '/admin/organizations/$id/{-$tab}', params: { id: 'new' } })
    }

    const handleOrganizationClick = (organization: PartnerService.Organization) => {
        void navigate({ to: '/admin/organizations/$id/{-$tab}', params: { id: organization.id } })
    }

    const visibleColumns = columns.filter(col => col.visible)

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold leading-7 text-foreground sm:truncate sm:text-3xl sm:tracking-tight">
                        {t('organizations.organizationManagement')}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {t('organizations.organizationManagementDescription')}
                    </p>
                </div>
                <button
                    onClick={handleCreateNewOrganization}
                    className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors flex items-center gap-2"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('organizations.createOrganization')}
                </button>
            </div>

            {/* Filters and Controls */}
            <OrganizationTableFilters
                filterText={filterText}
                setFilterText={setFilterText}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
                activeFilter={activeFilter}
                setActiveFilter={setActiveFilter}
                columns={columns}
                columnDropdownOpen={columnDropdownOpen}
                setColumnDropdownOpen={setColumnDropdownOpen}
                toggleColumnVisibility={toggleColumnVisibility}
            />

            {/* Error State */}
            {isError && (
                <div className="rounded-md bg-error/10 border border-error/20 p-4">
                    <div className="text-error text-sm">
                        {t('common.error')}
                    </div>
                </div>
            )}

            {/* Organizations Table */}
            {!isError && (
                <OrganizationsTable
                    organizations={filteredOrganizations}
                    totalOrganizations={organizations.length}
                    visibleColumns={visibleColumns}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    onOrganizationClick={handleOrganizationClick}
                    loadingMode={isInitialLoading}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMorePage}
                    isPageLoading={isFetchingNextPage}
                    partnersByOrgId={partnersByOrgId}
                    isPartnersLoading={isPartnersLoading}
                />
            )}
        </div>
    )
}
